import {
    LogErrorInput, LogLevel, LogLineInput, LogsInput, StoreLogsDocument,
} from "../../__generated__/api";
import client from "../../api";
import createId from "../../create-id";
import BaseError from "./BaseError";

const CACHE_KEY = "app.logs";
const MAX_CACHE_SIZE = 100;
const TIMER_RESET = 3000;
const MAX_CACHE_DELAY = 1000 * 60 * 60 * 24;
const MAX_TIMER = 1000 * 60 * 60 * 5;

interface CacheValue {
    date: Date;
    lines: LogLineInput[];
}

export function errorToInput(error: Error): LogErrorInput {
    const base = error as BaseError;
    return {
        message: error.message,
        name: error.name,
        stack: error.stack ?? "[Stack Unknown]",
        code: base.code,
        innerError: base.inner ? errorToInput(base.inner) : undefined,
        data: base.data,
    };
}

export function createContextData(component?: string) {
    return {
        currentPath: window.location.toString(),
        component,
    };
}

function deserializeDate(timestamp: Date | string) {
    return timestamp instanceof Date ? timestamp : new Date(timestamp);
}

export default class LogSaver {
    private messages: LogLineInput[] = [];

    private timer: ReturnType<typeof setTimeout> | undefined;

    private lastTimer = TIMER_RESET;

    private readonly sessionId: string = createId();

    private cache() {
        const snapshot = this.messages.sortByDescending(row => row.timestamp).limit(MAX_CACHE_SIZE);
        localStorage.setItem(CACHE_KEY, JSON.stringify({
            date: new Date(),
            lines: snapshot,
        } as CacheValue));
    }

    private async synchronize() {
        let snapshot = this.messages;
        this.messages = [];

        const cachedValues = localStorage.getItem(CACHE_KEY);
        if (cachedValues) {
            try {
                const cached = JSON.parse(cachedValues) as CacheValue;
                if (Date.now() - deserializeDate(cached.date).getTime() < MAX_CACHE_DELAY) {
                    snapshot = snapshot
                        .concat(cached.lines.map(row => ({
                            ...row,
                            timestamp: deserializeDate(row.timestamp),
                        })))
                        .dedupe(row => row.timestamp.getTime().toString());
                }
            } catch (err) {
                // guess we dont get the cache
            }

            localStorage.removeItem(CACHE_KEY);
        }
        try {
            if (snapshot.isNotEmpty) {
                await client.mutate<typeof StoreLogsDocument>({
                    mutation: StoreLogsDocument,
                    variables: {
                        logs: {
                            lines: snapshot,
                        } as LogsInput,
                    },
                });
            }
            this.lastTimer = TIMER_RESET;
        } catch (err) {
            this.messages = snapshot.concat(this.messages);
            this.messages.push({
                level: LogLevel.Error,
                message: "Error synchronizing logs",
                component: "logs",
                timestamp: new Date(),
                applicationSessionId: this.sessionId,
                data: {
                    ...createContextData(),
                    messageCount: snapshot.length,
                },
                error: errorToInput(err as Error),
            });
            this.lastTimer = Math.min(this.lastTimer * 2, MAX_TIMER);
            this.cache();
            this.resetTimer();
        }
    }

    private resetTimer() {
        if (this.timer) {
            clearTimeout(this.timer);
        }
        this.timer = setTimeout(async () => this.synchronize(), this.lastTimer);
    }

    public add(message: Omit<LogLineInput, "applicationSessionId">) {
        const withId = message as LogLineInput;
        withId.applicationSessionId = this.sessionId;
        this.messages.push(withId);
        this.resetTimer();
    }
}
